/**
 * Content for the tracking-led edition of the Precision Grip teaser.
 * Problem-led: the clinical tracking question comes first, the product second,
 * and the 12-minute saving lands last as a bonus.
 */

export const TRACKING_SECONDS = 30

/** Page 2 — the clinician's first-look poll. Order is deliberate: attachment last. */
export const pollOptions = ['Compliance', 'Staging', 'Attachment'] as const

export type PollOption = (typeof pollOptions)[number]

/**
 * Share of peers behind each answer. Values stay null until live poll data is
 * wired in — no invented percentages are shown.
 */
export const peerShare: Record<PollOption, number | null> = {
  Compliance: null,
  Staging: null,
  Attachment: null,
}

/** Page 3 — the chain between the digital plan and the delivered movement. */
export const translationChain = [
  'Digital plan',
  'Attachment',
  'Force',
  'Movement',
] as const

/** Page 3 / 4 — the three manual variables. */
export interface Deviation {
  name: string
  body: string
  image: string
  alt: string
}

export const deviations: Deviation[] = [
  {
    name: 'Shape',
    body: 'Bonded geometry differs from planned geometry.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/7cd350d7-6529-43e7-ae4f-aa5f5f174ad6.jpg',
    alt: 'Close-up of a bonded composite attachment whose form differs from the planned geometry',
  },
  {
    name: 'Position',
    body: 'The attachment engages in a different location.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/38bbd729-2284-4a08-b03b-532417a7cbd3.jpg',
    alt: 'Close-up of an attachment bonded away from its planned position on the tooth',
  },
  {
    name: 'Flash',
    body: 'Excess composite changes the attachment interface.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e88037e1-33db-49c4-bbfb-4b9662b9da38.jpg',
    alt: 'Close-up of composite flash remaining around the margin of a bonded attachment',
  },
]

/** Page 2 — clinical reference for a case that is not tracking as planned. */
export const trackingClip = {
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/7d8f1746-4a9f-4e60-9784-c9a6f1458802.jpg',
  alt: 'Clear aligner seated over a lower arch with a small visible gap at one tooth',
}

/** Clinician photography used to keep the story human. */
export const portraits = {
  clinician: {
    image:
      'https://cdn.magicpatterns.com/uploads/sHR3Z1vZEoUsShpMRHk4fe/Screenshot_2026-08-25_at_13.07.54.png',
    alt: 'Orthodontist seated, looking towards camera',
  },
  peers: {
    image:
      'https://cdn.magicpatterns.com/uploads/kmQS9GkkXCect7jQxzgfnd/Screenshot_2026-08-25_at_13.08.26.png',
    alt: 'Three clinical colleagues standing together',
  },
}

/** Page 4 — the force system closing visual. */
export const forceClip = {
  image:
    'https://cdn.magicpatterns.com/patterns/generated-images/7d946cc8-422d-479c-a369-396facbae11a.jpg',
  alt: 'Attachment on a premolar with the intended force direction and a slightly offset delivered direction',
}

/** Page 5 — the Precision Grip workflow. */
export interface WorkflowStep {
  name: string
  image: string
  alt: string
}

export const workflowSteps: WorkflowStep[] = [
  {
    name: 'Digitally planned',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/6f8b53ac-7d8d-4867-843c-f304efabc90e.jpg',
    alt: 'Digital design of the planned attachment',
  },
  {
    name: 'Custom 3D printed',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e604c62e-6b59-4547-9e01-43158c70bd19.jpg',
    alt: 'Pre-formed 3D printed attachment',
  },
  {
    name: 'Fully cured',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0ae5046c-50de-40e9-8d42-ad69a79186ea.jpg',
    alt: 'Fully cured Precision Grip attachment bonded to the tooth',
  },
  {
    name: 'Pre-loaded',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f1eee7cb-0c20-4a8c-8c14-889120f3507f.jpg',
    alt: 'Attachment pre-loaded in the transfer tray',
  },
]
